"use client";

import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Plane } from "lucide-react";
import type { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "@/@libs/strict-mode-droppable";
import {
  iHolidayQuoteItineraryBlock,
  type QuoteNarrativeLayoutVariant,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ItineraryBlock } from "../itinerary-section";
import {
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import {
  QUOTE_FLIGHTS_LAYOUT_OPTIONS,
  resolveFlightsLayout,
  type TripFlightsLayoutVariant,
} from "../quote-narrative-layout";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { cn } from "@/lib/utils";

interface TripModeFlightsSectionProps {
  flights: iHolidayQuoteItineraryBlock[];
  renderFlight?: (
    flight: iHolidayQuoteItineraryBlock,
    index: number,
    card: ReactNode,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => ReactNode;
  onLayoutChange?: (layout: QuoteNarrativeLayoutVariant) => Promise<void> | void;
  droppableId?: string;
  showEmpty?: boolean;
}

function FlightDetailBlock({
  flight,
  index,
  renderFlight,
  withAnchor = true,
  dragHandleProps,
  framed = false,
}: {
  flight: iHolidayQuoteItineraryBlock;
  index: number;
  renderFlight?: TripModeFlightsSectionProps["renderFlight"];
  withAnchor?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  /** Card chrome around the full standard flight details. */
  framed?: boolean;
}) {
  const card = (
    <div
      id={withAnchor ? `itinerary-${flight.id}` : undefined}
      className={cn(
        withAnchor ? "scroll-mt-28" : undefined,
        framed &&
          "overflow-hidden rounded-2xl border bg-white p-3 shadow-sm sm:p-4"
      )}
      style={
        framed ? { borderColor: quoteThemeCssVars.primary20 } : undefined
      }
      key={flight.id}
    >
      <ItineraryBlock item={flight} index={index} />
    </div>
  );
  return renderFlight ? (
    <>{renderFlight(flight, index, card, dragHandleProps)}</>
  ) : (
    card
  );
}

export function TripModeFlightsSection({
  flights,
  renderFlight,
  onLayoutChange,
  droppableId,
  showEmpty = false,
}: TripModeFlightsSectionProps) {
  const { quote } = useHolidayQuoteBrand();
  const savedLayout = resolveFlightsLayout(quote?.data?.tripFlightsLayout);
  const [layout, setLayout] = useState<TripFlightsLayoutVariant>(savedLayout);
  const [layoutSaving, setLayoutSaving] = useState(false);

  useEffect(() => {
    setLayout(savedLayout);
  }, [savedLayout]);

  useEffect(() => {
    const handleFocus = (event: Event) => {
      const blockId = (event as CustomEvent<{ blockId?: string }>).detail
        ?.blockId;
      if (!blockId || !flights.some((flight) => flight.id === blockId)) return;

      window.setTimeout(() => {
        document
          .getElementById("trip-section-flights")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        document
          .getElementById(`itinerary-${blockId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    };

    window.addEventListener("trip-focus-itinerary", handleFocus);
    return () =>
      window.removeEventListener("trip-focus-itinerary", handleFocus);
  }, [flights]);

  if (flights.length === 0) {
    if (!showEmpty) return null;
    return (
      <section
        id="trip-section-flights"
        className="scroll-mt-28 bg-white p-4 sm:p-6"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <h2
          className="flex items-center gap-2 text-xl sm:text-2xl font-semibold"
          style={{
            color: quoteThemeCssVars.blockHeading,
            fontFamily: quoteThemeCssVars.headingFont,
          }}
        >
          <Plane
            className="h-6 w-6"
            style={{ color: quoteThemeCssVars.primary }}
          />
          Flights
        </h2>
        <p className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          Add a flight to complete the travel section of your brochure.
        </p>
      </section>
    );
  }

  const handleLayoutChange = async (next: TripFlightsLayoutVariant) => {
    if (!onLayoutChange || next === layout) return;
    const previous = layout;
    setLayout(next);
    setLayoutSaving(true);
    try {
      await onLayoutChange(next);
    } catch {
      setLayout(previous);
    } finally {
      setLayoutSaving(false);
    }
  };

  const isCards = layout === "hotel_cards";

  const renderFlightItem = (
    flight: iHolidayQuoteItineraryBlock,
    index: number,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => (
    <FlightDetailBlock
      flight={flight}
      index={index}
      renderFlight={renderFlight}
      dragHandleProps={dragHandleProps}
      framed={isCards}
    />
  );

  const renderLayouts = () => {
    const listClassName = isCards
      ? "grid grid-cols-1 gap-5"
      : "space-y-6";

    if (droppableId) {
      return (
        <StrictModeDroppable droppableId={droppableId}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={listClassName}
            >
              {flights.map((flight, index) => (
                <Draggable
                  key={flight.id}
                  draggableId={flight.id}
                  index={index}
                >
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                    >
                      {renderFlightItem(
                        flight,
                        index,
                        dragProvided.dragHandleProps
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      );
    }

    return (
      <div className={listClassName}>
        {flights.map((flight, index) => renderFlightItem(flight, index))}
      </div>
    );
  };

  return (
    <section
      id="trip-section-flights"
      className="scroll-mt-28 bg-white p-4 sm:p-6 space-y-4"
      style={{
        borderRadius: quoteThemeCssVars.radiusCard,
        boxShadow: quoteThemeCssVars.shadowCard,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="flex items-center gap-2 text-xl sm:text-2xl font-semibold"
          style={{
            color: quoteThemeCssVars.blockHeading,
            fontFamily: quoteThemeCssVars.headingFont,
          }}
        >
          <Plane
            className="h-6 w-6"
            style={{ color: quoteThemeCssVars.primary }}
          />
          Flights
        </h2>

        {onLayoutChange ? (
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <span className="hidden sm:inline">Layout</span>
            <select
              className="rounded-lg border bg-white px-2 py-1.5 text-sm"
              value={layout}
              disabled={layoutSaving}
              onChange={(e) =>
                handleLayoutChange(e.target.value as TripFlightsLayoutVariant)
              }
            >
              {QUOTE_FLIGHTS_LAYOUT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {renderLayouts()}
    </section>
  );
}
